import React, { useState, useEffect, useRef } from 'react';
import { PageContainer, usePages, useGuideConfig } from '@ignitesales/neuro-application';
import GuidePageContainer from "@ignitesales/neuro-application/src/neuro-guide/guide-framework/components/GuidePageContainer";
import { useDialogueGuideSettings } from "../hooks/useDialogueGuideSettings.mjs";
import { useDialogueContext } from "../hooks/useDialogueContext.mjs";
import ContactFormPage from "./ContactFormPage";
import ActionService from '@ignitesales/neuro-application/src/neuro-guide/services/ActionService.mjs';
import { useAnswers } from '@ignitesales/neuro-application/src/neuro-guide/hooks';
import { useNeuroState } from '@ignitesales/neuro-application';
import {useBreakpoints} from "@ignitesales/neuro-application/src/neuro-ui/index.mjs";
import MobileLandingPage from "../mobile/pages/LandingPage";
import MobileSurveyPage from "../mobile/pages/SurveyPage";
import MobileSummaryPage from "../mobile/pages/SummaryPage";
import MobileNavigationBar from "../mobile/components/MobileNavigationBar";
import { useModal } from './ModalProvider';

/**
 * Component was made to override product page experience if we need to show contact page prior to recommendations
 */
const DialoguePageContainer = (props) => {
    const { contactFormCompleted, contactPageIsActive } = useDialogueContext();
    const { contactBeforeSummary, enableSharedMobileTheme } = useDialogueGuideSettings();
    const { productCart } = useNeuroState();
    const answerService = useAnswers();
    const answers = Object.values(answerService?.answers || {});
    const answerServiceRef = useRef(answerService);
    answerServiceRef.current = answerService;
    useEffect(() => { ActionService.cart = productCart; }, [productCart]);
    useEffect(() => { ActionService.answers = answers; }, [answers]);
    const { md: isLargeScreen } = useBreakpoints();
    const isMobile = !isLargeScreen;
    const shouldUseMobile = isMobile && enableSharedMobileTheme;
    const shouldShowContact =  contactBeforeSummary === true && !contactFormCompleted && contactPageIsActive;
    const contactPageOverride = shouldShowContact
        ? { SummaryPage: ContactFormPage, DetailsPage: ContactFormPage, RemoteSummaryPage: ContactFormPage }
        : {};
    const mobilePageOverride = shouldUseMobile
        ? { 
            LandingPage: MobileLandingPage,
            SurveyPage: MobileSurveyPage,
            SummaryPage: MobileSummaryPage,
            DetailsPage: MobileSummaryPage
          }
        : {};
    const { currentPageUrl, getCurrentPage } = usePages();
    const { dialogueApiConfig, layouts } = useGuideConfig();
    // A null page object is valid on landing/summary pages. Track whether a
    // lifecycle has started separately from the page data itself.
    const previousPage = useRef(null);
    const lifecycleQueue = useRef(Promise.resolve());
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const { openModal, clearAll } = useModal();
    const [pendingPopupActions, setPendingPopupActions] = useState([]);
    const [pendingSlideActions, setPendingSlideActions] = useState([]);

    const currentPage = getCurrentPage();
    const pageKey = currentPage?.page?.key;

    const collectActions = async (triggerType, page, pageUrl, pageKey, navigationAnswers, navigationCart) => {
        // Always run tick - it handles null pages via URL fallback in getPageName()
        await ActionService.tick(triggerType, page, pageUrl, navigationAnswers, navigationCart);

        // Handle pop/slide actions
        const actions = await ActionService.getActionsByTriggerType(triggerType, ActionService.getPageName(page, pageUrl), pageKey);
        const popActions = [];
        const slideActions = [];

        if (actions && Array.isArray(actions)) {
            for (const action of actions) {
                const trigger = await ActionService.extractTriggerByTriggerType(action, triggerType, ActionService.getPageName(page, pageUrl), pageKey);
                if (!trigger || (trigger.fireOnce && ActionService.checkIfActionFiredOnce(action, trigger))) {
                    continue;
                }

                if (action.actionType === 'pop') {
                    popActions.push({ action, trigger });
                } else if (action.actionType === 'slide') {
                    slideActions.push({ action, trigger });
                }
            }
        }
        return { popActions, slideActions };
    };

    const processActions = async (...args) => {
        try {
            return await collectActions(...args);
        } catch (error) {
            // A failed lookup must not suppress the next page's lifecycle.
            console.error('DialoguePageContainer: Error processing lifecycle actions:', error);
            return { popActions: [], slideActions: [] };
        }
    };

    // Record navigation before awaiting requests, then process transitions in order.
    // Slow actions must not make the next transition reuse an older departing page.
    useEffect(() => {
        if (!dialogueApiConfig) return;

        const page = { page: currentPage, url: currentPageUrl, key: pageKey };
        const previous = previousPage.current;
        const navigationAnswers = answers;
        const navigationCart = productCart;
        const pathname = url => (url || '').split(/[?#]/)[0].replace(/\/$/, '');
        if (previous && pathname(previous.url) === pathname(page.url) && previous.key === page.key) return;
        previousPage.current = page;

        // Navigation dismisses the old UI without completing its actions.
        clearAll();
        setPendingPopupActions([]);
        setPendingSlideActions([]);

        lifecycleQueue.current = lifecycleQueue.current.then(async () => {
            if (!isMounted.current) return;

            const leaving = previous
                ? await processActions('onLeave', previous.page, previous.url, previous.key, navigationAnswers, navigationCart)
                : { popActions: [], slideActions: [] };
            const entering = await processActions('onLoad', page.page, page.url, page.key, navigationAnswers, navigationCart);

            // Complete run actions for every transition, but only show UI belonging
            // to the latest page. Ignore responses arriving after unmount.
            if (!isMounted.current || previousPage.current !== page) return;
            const popActions = [...leaving.popActions, ...entering.popActions];
            const slideActions = [...leaving.slideActions, ...entering.slideActions];
            if (layouts) {
                [...popActions, ...slideActions].forEach(({ action, trigger }) =>
                    openModal(action, { trigger, answers: navigationAnswers }));
            } else {
                setPendingPopupActions(popActions.map(entry => ({ ...entry, answers: navigationAnswers })));
                setPendingSlideActions(slideActions.map(entry => ({ ...entry, answers: navigationAnswers })));
            }
        }).catch(error => {
            console.error('DialoguePageContainer: Error processing lifecycle actions:', error);
        });
    }, [currentPageUrl, pageKey, dialogueApiConfig]);

    // When layouts become available, apply pending popup/slide actions
    useEffect(() => {
        try {
            if (!layouts) return;

            if (pendingPopupActions.length > 0 || pendingSlideActions.length > 0) {
                [...pendingPopupActions, ...pendingSlideActions].forEach(({ action, trigger, answers: actionAnswers }) =>
                    openModal(action, { trigger, answers: actionAnswers }));
                setPendingPopupActions([]);
                setPendingSlideActions([]);
            }
        } catch (e) {
            console.error('DialoguePageContainer: Error applying pending actions:', e);
        }
    }, [layouts, pendingPopupActions, pendingSlideActions]);

    useEffect(() => {
      if (enableSharedMobileTheme) {
        document.body.classList.add("mobile-theme");
      } else {
        document.body.classList.remove("mobile-theme");
      }

      return () => {
        document.body.classList.remove("mobile-theme");
      };
    }, [enableSharedMobileTheme]);

    // Widget actions open through the shared modal owner.
    useEffect(() => {
        const handleTriggerAction = async (e) => {
            const { actionId } = e.detail || {};
            if (!actionId) return;

            try {
                const action = await ActionService.getActionById(actionId);
                if (!action) return;

                const currentAnswers = Object.values(answerServiceRef.current?.answers || {});

                // Find the first trigger whose conditions are fulfilled
                let trigger = null;
                for (const t of (action.triggers || [])) {
                    if (await ActionService.allConditionsFulfilled(t.conditions, currentAnswers)) {
                        trigger = t;
                        break;
                    }
                }
                if (!trigger) return;

                if (trigger.fireOnce && ActionService.checkIfActionFiredOnce(action, trigger)) {
                    return;
                }

                if (action.actionType === 'pop' || action.actionType === 'slide') {
                    openModal(action, { trigger, answers: currentAnswers });
                } else {
                    const params = await ActionService.extractActionParams(action);
                    await ActionService.triggerAction(action, trigger._id, params);
                }
            } catch (error) {
                console.error('DialoguePageContainer: Error handling triggerAction event:', error);
            }
        };

        window.addEventListener('ignite:triggerAction', handleTriggerAction);

        return () => {
            window.removeEventListener('ignite:triggerAction', handleTriggerAction);
        };
    }, [openModal]);

    return (
        <>
            <PageContainer {...props} {...mobilePageOverride} {...contactPageOverride} />
            {shouldUseMobile && <MobileNavigationBar />}
        </>
    );
};
DialoguePageContainer.propTypes = {
    ...GuidePageContainer.propTypes
};

export default DialoguePageContainer;
